import { useEffect, useRef } from "react";

import { graphql, useFragment } from "react-relay";

import {
  parseApiExplorer,
  useApiExplorer,
} from "app/contexts/ApiExplorerContext"; // avoiding barrel export to satisfy relay

import type { ApiExplorerSync_query$key } from "./__generated__/ApiExplorerSync_query.graphql";

/** Props for the ApiExplorerSync component */
interface Props {
  /** Bootstrap query ref carrying the saved API explorer `userData`. */
  queryRef: ApiExplorerSync_query$key;
}

/**
 * Reconciles the locally-cached API explorer preference with the value persisted
 * to `userData`. The server is the cross-device source of truth, so on load its
 * value wins and is written back to localStorage. Because every local change is
 * also persisted server-side, the server value is always at least as fresh as
 * the last change made on any device. Renders nothing.
 *
 * The preference rides along with the bootstrap `AppQuery` (see its
 * `...ApiExplorerSync_query` spread) rather than issuing its own request, so it
 * costs no extra round-trip and cannot fail independently of the app load.
 */
export function ApiExplorerSync({ queryRef }: Props) {
  const { apiExplorer, setApiExplorer } = useApiExplorer();
  const hasSynced = useRef(false);

  const data = useFragment(
    graphql`
      fragment ApiExplorerSync_query on Query {
        apiExplorer: userData(
          filterElement: {
            single: { name: "name", operator: "equals", value: "api_explorer" }
          }
        ) {
          edges {
            node {
              value
            }
          }
        }
      }
    `,
    queryRef,
  );

  const serverExplorer = parseApiExplorer(
    data.apiExplorer.edges[0]?.node.value,
  );

  useEffect(() => {
    if (hasSynced.current || serverExplorer === null) {
      return;
    }
    hasSynced.current = true;
    if (serverExplorer !== apiExplorer) {
      setApiExplorer(serverExplorer);
    }
  }, [serverExplorer, apiExplorer, setApiExplorer]);

  return null;
}
